// src/lib/components/molecules/EditSphere/EditSphere.tsx

import React, { useState, useEffect, useCallback } from 'react';
import SphereEditorOrganism from '../../../organisms/SphereEditor/SphereEditor';
import { Sphere, UpdateAck } from 'types/global';
import { getSphereById, updateSphere, UpdateSphereError } from '@center-platform/spheres/sphere';
import { PermissionList } from 'types/permissions';
import { Skeleton } from 'primereact/skeleton';
import './EditSphere.scss';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';

interface EditSphereProps {
  sphere: Sphere;
  parentSphere: Sphere;
  /** Called after the form saves; callers close the editor here. */
  onSave: (updatedSphere: Sphere, ancestorIds?: string[]) => void;
  /** Called when the sphere changed while the editor stays open (a status factor, its glyph or picture). */
  onSphereRefresh?: (sphereId: string) => void;
  editable?: boolean;
  onClose: () => void;
  showTabs?: boolean;
  viewMode?: boolean;
  fetchPermissionList: (sphereId: string, nodeLabel: string, permissions?: string[]) => Promise<PermissionList | null>;
  initialTab?: string; // Tab ID to open initially (e.g., 'status-factors-tab')
  /** If true, skip the initial re-fetch — caller already provided fresh data with skipCache */
  skipInitialFetch?: boolean;
}

const EditSphere: React.FC<EditSphereProps> = ({
  sphere,
  parentSphere,
  onSave,
  onSphereRefresh,
  editable = true,
  onClose,
  showTabs = true,
  viewMode = false,
  fetchPermissionList,
  initialTab,
  skipInitialFetch = false,
}) => {
  
  const apiClients = useApiClientsOrNull();
  
  if (!apiClients) {
    return null;
  }
  
  const { graphClient } = apiClients;
  const parentSphereType = Array.isArray(parentSphere.properties.type)
    ? parentSphere.properties.type[0]
    : parentSphere.properties.type;

  const [sphereToEdit, setSphereToEdit] = useState<Sphere | null>(null);
  const [isSphereSubmitting, setIsSphereSubmitting] = useState(false);

  // fetch fresh sphere (skipped when caller already provided fresh data)
  useEffect(() => {
    if (skipInitialFetch) {
      // Caller fetched with skipCache — use sphere prop directly
      setSphereToEdit(sphere);
      return;
    }
    (async () => {
      const resp = await getSphereById(graphClient, sphere.id, true, true, true);
      if (!resp.success) return;
      const fresh = resp.data.sphere;
      // preserve connectingPerspectives & urgency
      fresh.connectingPerspectives = sphere.connectingPerspectives;
      const centerCenter = sphere.connectingPerspectives?.find((p) => p.system_name === 'center_center')?.urgency;
      if (centerCenter) fresh.properties.urgency = centerCenter;
      setSphereToEdit(fresh);
    })();
  }, [sphere.id, graphClient, skipInitialFetch]);

  // 2️⃣ handleUpdate now passes apiClient first
  // EditSphere.tsx  – inside the component
  const handleUpdate = useCallback(async (updatedSphere: Sphere): Promise<UpdateAck> => {
    if (!sphereToEdit) {
      return { success: false, message: 'No sphere loaded' };
    }

    setIsSphereSubmitting(true);

    try {
      const result = await updateSphere(graphClient, {
        sphereId: sphere.id,
        oldSphere: sphereToEdit,
        updatedSphere,
      });

      // Success → refresh caller and acknowledge
      onSave(result.sphere, result.ancestorIds);
      return { success: true };
    } catch (err: unknown) {
      // Convert any error into a message string
      let msg: string;
      if (err instanceof UpdateSphereError) {
        msg = `${err.message}: ${Array.isArray(err.details) ? err.details.join(', ') : err.details}`;
      } else {
        msg = (err as Error).message ?? 'Unknown error';
      }

      console.error('Error updating sphere:', msg);
      return { success: false, message: msg };
    } finally {
      setIsSphereSubmitting(false);
    }
  }, [sphereToEdit, graphClient, sphere.id, onSave]);

  const handleStatusFactorsChanged = useCallback(() => {
    onSphereRefresh?.(sphere.id);
  }, [sphere.id, onSphereRefresh]);

  if (!sphereToEdit) {
    return (
      <div style={{ height: '100%' }}>
        <Skeleton width="100%" height="50px" className="mb-2" />
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Skeleton width="80%" height="450px" borderRadius="20px" />
          <Skeleton width="20%" height="450px" borderRadius="20px" />
        </div>
        <div className="flex mt-3 justify-content-end">
          <Skeleton width="100px" height="40px" borderRadius="20px" />
          <Skeleton width="100px" height="40px" borderRadius="20px" className="ml-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="add-sphere">
      <SphereEditorOrganism
        sphere={sphereToEdit}
        onUpdate={handleUpdate}
        editable={editable}
        parentSphereType={parentSphereType}
        onClose={onClose}
        isSphereSubmitting={isSphereSubmitting}
        showTabs={showTabs}
        viewMode={viewMode}
        fetchPermissionList={fetchPermissionList}
        initialTab={initialTab}
        onStatusFactorsChanged={handleStatusFactorsChanged}
        onSphereChanged={onSphereRefresh}
      />
    </div>
  );
};

export default EditSphere;
